import React, { useEffect, useState, Children } from 'react';
import { PlusIcon, SearchIcon, FilterIcon, EyeIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, BuildingIcon, MapPinIcon, GlobeIcon, ChevronLeftIcon, ChevronRightIcon, BarChartIcon, Edit2Icon, Trash2Icon, NetworkIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { VendorCard } from './VendorCard';
export function VendorList({
  vendors,
  onViewVendor,
  setCurrentView,
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const [esgFilter, setESGFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isESGEnabled] = useState(true);
  const [hasESGViewPermission] = useState(true);
  const [showESGColumn, setShowESGColumn] = useState(true);
  // Check for status filter from dashboard on mount
  useEffect(() => {
    const savedFilter = sessionStorage.getItem('vendorStatusFilter');
    if (savedFilter) {
      setStatusFilter(savedFilter);
      // Clear the filter after applying it
      sessionStorage.removeItem('vendorStatusFilter');
    }
  }, []);
  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = (vendor.vendorName || '').toLowerCase().includes(searchTerm.toLowerCase()) || (vendor.vendorId || '').toLowerCase().includes(searchTerm.toLowerCase()) || (vendor.vendorCode || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || vendor.vendorStatus?.name === statusFilter;
    let matchesCompliance = true;
    if (complianceFilter !== 'all') {
      if (complianceFilter === 'Compliant') {
        matchesCompliance = vendor.isCompliant === true;
      } else if (complianceFilter === 'Non-Compliant') {
        matchesCompliance = vendor.isCompliant === false;
      }
    }
    let matchesESG = true;
    if (isESGEnabled && esgFilter !== 'all') {
      if (esgFilter === 'not-provided') {
        matchesESG = !vendor.esg || !vendor.esg.score;
      } else if (vendor.esg && vendor.esg.score !== undefined) {
        if (esgFilter === 'excellent') matchesESG = vendor.esg.score >= 80;else if (esgFilter === 'good') matchesESG = vendor.esg.score >= 60 && vendor.esg.score < 80;else if (esgFilter === 'moderate') matchesESG = vendor.esg.score >= 40 && vendor.esg.score < 60;else if (esgFilter === 'needs-review') matchesESG = vendor.esg.score < 40;
      } else {
        matchesESG = false;
      }
    }
    return matchesSearch && matchesStatus && matchesCompliance && matchesESG;
  });
  const totalVendors = filteredVendors.length;
  const totalPages = Math.ceil(totalVendors / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalVendors);
  const currentVendors = filteredVendors.slice(startIndex, endIndex);
  const getAvailabilityText = vendor => {
    if (!vendor.orgUnitId) {
      return 'All Entities';
    }
    if (vendor.includeChildOrgUnits) {
      return `${vendor.orgUnit?.name || 'OrgUnit'} + Children`;
    }
    return vendor.orgUnit?.name || 'Specific OrgUnit';
  };
  const getAvailabilityIcon = vendor => {
    if (!vendor.orgUnitId) {
      return <GlobeIcon className="h-4 w-4 text-blue-500" data-id="element-22467" />;
    }
    if (vendor.includeChildOrgUnits) {
      return <NetworkIcon className="h-4 w-4 text-purple-500" data-id="element-22468" />;
    }
    return <BuildingIcon className="h-4 w-4 text-indigo-500" data-id="element-22469" />;
  };
  const getAvailabilityTooltip = vendor => {
    if (!vendor.orgUnitId) {
      return 'Available to all entities globally';
    }
    if (vendor.includeChildOrgUnits) {
      return `Available to ${vendor.orgUnit?.name} and all child org units`;
    }
    return `Available only to ${vendor.orgUnit?.name}`;
  };
  const getComplianceBadge = vendor => {
    if (vendor.isCompliant) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-22470">
          <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-22471" />
          Compliant
        </span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-22472">
          <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-22473" />
          Non-Compliant
        </span>;
    }
  };
  const getESGStatusBadge = vendor => {
    if (!vendor.esg || vendor.esg.score === undefined) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-22474">
          Not Provided
        </span>;
    }
    const score = vendor.esg.score;
    if (score >= 80) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-22475">
          <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-22476" />
          Excellent
        </span>;
    } else if (score >= 60) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-22477">
          <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-22478" />
          Good
        </span>;
    } else if (score >= 40) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-22479">
          <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-22480" />
          Moderate
        </span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-22481">
          <XCircleIcon className="mr-1 h-3 w-3" data-id="element-22482" />
          Needs Review
        </span>;
    }
  };
  const goToPage = page => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };
  const handlePageSizeChange = e => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
  };
  // Show active filter indicator
  const hasActiveFilters = statusFilter !== 'all' || complianceFilter !== 'all' || esgFilter !== 'all' || searchTerm !== '';
  return <div data-id="element-22483">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-22484">
        <div className="flex-1 max-w-md" data-id="element-22485">
          <div className="relative rounded-md shadow-sm" data-id="element-22486">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-22487">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-22488" />
            </div>
            <input type="text" value={searchTerm} onChange={e => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search vendors..." data-id="element-22489" />
          </div>
        </div>
        <div className="flex space-x-2" data-id="element-22490">
          <div data-id="element-22491">
            <select value={statusFilter} onChange={e => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-22492">
              <option value="all" data-id="element-22493">All Statuses</option>
              <option value="Active" data-id="element-22494">Active</option>
              <option value="Pending Onboarding" data-id="element-22495">Pending Onboarding</option>
              <option value="Inactive" data-id="element-22496">Inactive</option>
              <option value="Invited" data-id="element-22497">Invited</option>
              <option value="Suspended" data-id="element-22498">Suspended</option>
            </select>
          </div>
          <div data-id="element-22499">
            <select value={complianceFilter} onChange={e => {
            setComplianceFilter(e.target.value);
            setCurrentPage(1);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-22500">
              <option value="all" data-id="element-22501">All Compliance</option>
              <option value="Compliant" data-id="element-22502">Compliant</option>
              <option value="Non-Compliant" data-id="element-22503">Non-Compliant</option>
            </select>
          </div>
          {isESGEnabled && hasESGViewPermission && <div data-id="element-22504">
              <select value={esgFilter} onChange={e => {
            setESGFilter(e.target.value);
            setCurrentPage(1);
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-22505">
                <option value="all" data-id="element-22506">All ESG Status</option>
                <option value="excellent" data-id="element-22507">Excellent (80+)</option>
                <option value="good" data-id="element-22508">Good (60-79)</option>
                <option value="moderate" data-id="element-22509">Moderate (40-59)</option>
                <option value="needs-review" data-id="element-22510">Needs Review (&lt;40)</option>
                <option value="not-provided" data-id="element-22511">Not Provided</option>
              </select>
            </div>}
          <div className="inline-flex shadow-sm rounded-md" data-id="element-22512">
            <button onClick={() => setViewMode('table')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-l-md ${viewMode === 'table' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-22513">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-22514">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" data-id="element-22515" />
              </svg>
            </button>
            <button onClick={() => setViewMode('grid')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-r-md ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-22516">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-22517">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" data-id="element-22518" />
              </svg>
            </button>
          </div>
          <button onClick={() => setCurrentView('onboard-vendor')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22519">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-22520" />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Active filter indicator */}
      {hasActiveFilters && <div className="mb-4 flex items-center gap-2" data-id="element-22521">
          <span className="text-sm text-gray-600" data-id="element-22522">Active filters:</span>
          {statusFilter !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-22523">
              Status: {statusFilter}
              <button onClick={() => setStatusFilter('all')} className="ml-1 inline-flex items-center" data-id="element-22524">
                <XCircleIcon className="h-3 w-3" data-id="element-22525" />
              </button>
            </span>}
          {complianceFilter !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-22526">
              Compliance: {complianceFilter}
              <button onClick={() => setComplianceFilter('all')} className="ml-1 inline-flex items-center" data-id="element-22527">
                <XCircleIcon className="h-3 w-3" data-id="element-22528" />
              </button>
            </span>}
          {esgFilter !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-22529">
              ESG: {esgFilter}
              <button onClick={() => setESGFilter('all')} className="ml-1 inline-flex items-center" data-id="element-22530">
                <XCircleIcon className="h-3 w-3" data-id="element-22531" />
              </button>
            </span>}
          {searchTerm && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-22532">
              Search: "{searchTerm}"
              <button onClick={() => setSearchTerm('')} className="ml-1 inline-flex items-center" data-id="element-22533">
                <XCircleIcon className="h-3 w-3" data-id="element-22534" />
              </button>
            </span>}
          <button onClick={() => {
        setStatusFilter('all');
        setComplianceFilter('all');
        setESGFilter('all');
        setSearchTerm('');
      }} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-22535">
            Clear all
          </button>
        </div>}

      {isESGEnabled && hasESGViewPermission && <div className="mb-4 flex items-center" data-id="element-22536">
          <label className="flex items-center text-sm text-gray-700" data-id="element-22537">
            <input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={showESGColumn} onChange={e => setShowESGColumn(e.target.checked)} data-id="element-22538" />
            <span className="ml-2" data-id="element-22539">Show ESG Score column</span>
          </label>
        </div>}

      <div className="flex justify-between items-center mb-4" data-id="element-22540">
        <div className="text-sm text-gray-700" data-id="element-22541">
          Showing <span className="font-medium" data-id="element-22542">{startIndex + 1}</span> to{' '}
          <span className="font-medium" data-id="element-22543">{endIndex}</span> of{' '}
          <span className="font-medium" data-id="element-22544">{totalVendors}</span> vendors
        </div>
        <div className="flex items-center space-x-2" data-id="element-22545">
          <span className="text-sm text-gray-700" data-id="element-22546">Rows per page:</span>
          <select value={pageSize} onChange={handlePageSizeChange} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-22547">
            <option value={10} data-id="element-22548">10</option>
            <option value={25} data-id="element-22549">25</option>
            <option value={50} data-id="element-22550">50</option>
            <option value={100} data-id="element-22551">100</option>
          </select>
        </div>
      </div>

      {viewMode === 'table' && <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-22552">
          {currentVendors.length === 0 ? <div className="px-4 py-8 text-center text-gray-500" data-id="element-22553">
              No vendors found matching your search criteria.
            </div> : <div className="overflow-x-auto" data-id="element-22554">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-22555">
                <thead className="bg-gray-50" data-id="element-22556">
                  <tr data-id="element-22557">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22558">
                      Vendor
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22559">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22560">
                      Availability
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22561">
                      Accounts
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22562">
                      Country
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22563">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22564">
                      Compliance
                    </th>
                    {isESGEnabled && hasESGViewPermission && showESGColumn && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22565">
                        ESG Score
                      </th>}
                    <th scope="col" className="sticky right-0 bg-gray-50 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider shadow-sm" data-id="element-22566">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-22567">
                  {currentVendors.map(vendor => <tr key={vendor.vendorId} className="hover:bg-gray-50" data-id="element-22568">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-22569">
                        <div className="text-sm font-medium text-indigo-600" data-id="element-22570">
                          {vendor.vendorName}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-22571">
                          {vendor.vendorCode || vendor.vendorId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-22572">
                        {vendor.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-22573">
                        <div className="flex items-center text-sm text-gray-500" title={getAvailabilityTooltip(vendor)} data-id="element-22574">
                          {getAvailabilityIcon(vendor)}
                          <span className="ml-1" data-id="element-22575">
                            {getAvailabilityText(vendor)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-22576">
                        {(vendor.accounts?.length || 0) > 0 ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-22577">
                            {vendor.accounts.length}
                          </span> : <span className="text-sm text-gray-400" data-id="element-22578">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-22579">
                        {vendor.country}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-22580">
                        <StatusBadge status={vendor.vendorStatus?.code?.toLowerCase() || 'active'} text={vendor.vendorStatus?.name || 'Active'} data-id="element-22581" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-22582">
                        {getComplianceBadge(vendor)}
                      </td>
                      {isESGEnabled && hasESGViewPermission && showESGColumn && <td className="px-6 py-4 whitespace-nowrap" data-id="element-22583">
                            {vendor.esg ? <div className="flex items-center" data-id="element-22584">
                                <span className="text-sm font-medium mr-2" data-id="element-22585">
                                  {vendor.esg.score !== undefined ? vendor.esg.score : 'N/A'}
                                </span>
                                {vendor.esg.score !== undefined && getESGStatusBadge(vendor)}
                              </div> : <span className="text-sm text-gray-500" data-id="element-22586">N/A</span>}
                          </td>}
                      <td className="sticky right-0 bg-white px-6 py-4 whitespace-nowrap text-center shadow-sm" data-id="element-22587">
                        <div className="flex items-center justify-center space-x-2" data-id="element-22588">
                          <button onClick={() => {
                    onViewVendor(vendor);
                    // Set editing mode flag
                    sessionStorage.setItem('vendorEditMode', 'true');
                  }} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-22589">
                            <Edit2Icon className="h-4 w-4" data-id="element-22590" />
                          </button>
                          <button onClick={() => onViewVendor(vendor)} className="text-green-600 hover:text-green-900" title="View Details" data-id="element-22591">
                            <EyeIcon className="h-4 w-4" data-id="element-22592" />
                          </button>
                          <button onClick={() => {
                    if (window.confirm(`Are you sure you want to delete ${vendor.vendorName}?`)) {
                      console.log('Delete vendor:', vendor.vendorId);
                      // In a real app, this would call a delete handler
                    }
                  }} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-22593">
                            <Trash2Icon className="h-4 w-4" data-id="element-22594" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}

      {viewMode === 'grid' && <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-22595">
          {currentVendors.length === 0 ? <div className="col-span-full text-center py-12 bg-white rounded-lg shadow" data-id="element-22596">
              <div className="mx-auto h-12 w-12 text-gray-400 flex items-center justify-center" data-id="element-22597">
                <BuildingIcon className="h-12 w-12" data-id="element-22598" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-22599">
                No vendors found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-22600">
                Try adjusting your search or filters.
              </p>
              <div className="mt-6" data-id="element-22601">
                <button onClick={() => setCurrentView('onboard-vendor')} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-22602">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-22603" />
                  Add Vendor
                </button>
              </div>
            </div> : currentVendors.map(vendor => <VendorCard key={vendor.vendorId} vendor={vendor} onViewVendor={onViewVendor} getAvailabilityIcon={getAvailabilityIcon} getAvailabilityText={getAvailabilityText} locale={locale} isESGEnabled={isESGEnabled && hasESGViewPermission && showESGColumn} data-id="element-22604" />)}
        </div>}

      {totalVendors > 0 && <div className="mt-4 flex items-center justify-between" data-id="element-22605">
          <div className="flex-1 flex justify-between sm:hidden" data-id="element-22606">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-22607">
              Previous
            </button>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-22608">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-22609">
            <div data-id="element-22610">
              <p className="text-sm text-gray-700" data-id="element-22611">
                Page <span className="font-medium" data-id="element-22612">{currentPage}</span> of{' '}
                <span className="font-medium" data-id="element-22613">{totalPages}</span>
              </p>
            </div>
            <div data-id="element-22614">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-22615">
                <button onClick={() => goToPage(1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-22616">
                  <span className="sr-only" data-id="element-22617">First Page</span>
                  <div className="h-5 w-5" aria-hidden="true" data-id="element-22618" />
                </button>
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-22619">
                  <span className="sr-only" data-id="element-22620">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-22621" />
                </button>
                {Array.from({
              length: Math.min(5, totalPages)
            }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return <button key={pageNum} onClick={() => goToPage(pageNum)} className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum ? 'bg-indigo-50 border-indigo-500 text-indigo-600 z-10' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-22622">
                        {pageNum}
                      </button>;
            })}
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-22623">
                  <span className="sr-only" data-id="element-22624">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-22625" />
                </button>
                <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-22626">
                  <span className="sr-only" data-id="element-22627">Last Page</span>
                  <div className="h-5 w-5" aria-hidden="true" data-id="element-22628" />
                </button>
              </nav>
            </div>
          </div>
        </div>}
    </div>;
}